import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";

const Col = ({ title, links, testId }) => (
  <div data-testid={testId} className="md:col-span-2">
    <h4 className="eyebrow">{title}</h4>
    <ul className="mt-4 space-y-2 text-sm text-neutral-400">
      {links.map(([l, to]) => <li key={to}><Link to={to} className="transition-colors hover:text-white">{l}</Link></li>)}
    </ul>
  </div>
);

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/company").then((r) => setC(r.data)).catch(() => {}); }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/subscribe", { email });
      toast.success("You're on the list.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const socials = [[Instagram, c?.instagram], [Facebook, c?.facebook], [Linkedin, c?.linkedin]];
  return (
    <footer className="relative z-10 border-t border-neutral-800 bg-black">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-12">
        <div className="md:col-span-4">
          <Logo className="h-16" />
          <p className="mt-4 max-w-xs text-sm text-neutral-400">A request-first live entertainment house — trail rides, concerts, corporate events, sponsorships, vendor marketplaces, artist bookings and event-management consulting.</p>
          <div className="mt-5 flex gap-3">
            {socials.map(([Icon, href], i) => (
              <a key={i} href={href || "#"} target={href ? "_blank" : undefined} rel="noreferrer" aria-label="social"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-700 text-neutral-300 transition-colors hover:border-white hover:text-white" data-testid={`social-link-${i}`}>
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
        <Col testId="footer-explore" title="Explore" links={[["Upcoming Events", "/events"], ["Six Lanes", "/services"], ["Digital Products", "/products"], ["Artists", "/artists"], ["Sponsorships", "/sponsorships"], ["Vendor Booths", "/vendors"], ["Start a Request", "/request"]]} />
        <Col testId="footer-company" title="Company" links={[["About EON", "/company"], ["Contact", "/contact"], ["Privacy Policy", "/privacy"], ["Terms of Service", "/terms"], ["Refund & Weather Policy", "/refund-policy"], ["Service & Refund Policy", "/service-policy"], ["Find My Ticket", "/ticket-lookup"], ["Owner's Manual", "/owner-manual"], ["Organizer Login", "/admin/login"]]} />
        <div className="md:col-span-4">
          <h4 className="eyebrow">Business</h4>
          {c && (
            <address className="mt-4 space-y-1 text-sm not-italic text-neutral-400" data-testid="footer-business-info">
              <div className="font-semibold text-neutral-200">{c.legal_name}</div>
              {c.address && <div>{c.address}</div>}
              <div>{c.city}, {c.state} {c.zip}</div>
              {c.phone && <div><a href={`tel:${c.phone}`} className="hover:text-white">{c.phone}</a></div>}
              {c.email && <div><a href={`mailto:${c.email}`} className="hover:text-white">{c.email}</a></div>}
              <div className="text-xs text-neutral-500">{c.hours}</div>
            </address>
          )}
          <form onSubmit={subscribe} className="mt-5 flex gap-2" data-testid="footer-subscribe-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="footer-subscribe-input"
              className="w-full rounded-full border border-neutral-700 bg-black/50 px-4 py-2.5 text-sm text-stone-100 outline-none focus:border-white" />
            <button type="submit" disabled={busy} data-testid="footer-subscribe-btn" className="btn-chrome whitespace-nowrap px-5 py-2.5 text-[11px] disabled:opacity-50">Join</button>
          </form>
        </div>
      </div>
      <div className="border-t border-neutral-800 py-5 text-center font-mono-label text-[11px] uppercase tracking-widest text-stone-600">
        © {new Date().getFullYear()} {c?.legal_name || "EON Infinity Entertainment LLC"} — All Rights Reserved
      </div>
    </footer>
  );
};
